import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import {
  ALL_REPORT_DELIVERY_METHODS,
  ALL_REPORT_DELIVERY_PURPOSES,
  REPORT_DELIVERY_PURPOSE,
  REPORT_VERSION_STATUS,
  type ReportDeliveryMethod,
  type ReportDeliveryPurpose,
  type ReportVersionStatus,
} from '@lims/shared-types';
import {
  recordReportDeliverySchema,
  releasePaymentHoldSchema,
  type RecordReportDeliveryInput,
} from '@lims/shared-validation';
import { clientsApi, reportsApi, type ReportDetailView } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { useLaboratoryTimeZone } from '../../app/laboratory-time-zone.js';
import { formatDateTime } from '../../components/format.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  CheckboxField,
  DateTimeField,
  Dialog,
  RadioField,
  SelectField,
  TextAreaField,
  TextField,
  useToast,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { isTooFarAhead } from './report-dates.js';
import { MAXIMUM_REASON, textProblem, useReportAct } from './report-lifecycle-actions.js';
import { ReportRefusalNotice } from './report-parts.js';

/**
 * Recording deliveries and notices, and releasing a payment hold — sub-phase 10c.
 *
 * ===========================================================================
 * THE SYSTEM SENDS NOTHING. EVERY ROW HERE RECORDS WHAT A PERSON DID.
 * ===========================================================================
 *
 * Issuance is not delivery (CLAUDE.md section 8, D121). A delivery states that
 * somebody handed the document over, by a method outside this product, at a
 * moment THEY state; a notice states that somebody told a party about a
 * suspension, cancellation, replacement or reinstatement. Nothing is emailed or
 * posted by pressing a button, and the confirmation says "recorded", never
 * "sent" — `sentBySystem` is false on every row because that is the actual
 * send result.
 *
 * Receipt is not assumed: it is confirmed only with the evidence it rests on.
 *
 * A payment hold restricts a DELIVERY and the public download, and nothing
 * else (D115). Releasing it changes access only.
 *
 * Every control is offered from the server's `can.*` hints and every route
 * re-checks the permission and the state (section 5.1).
 */

/** Nothing chosen in an optional select. Never a uuid, so it never reaches a body. */
const NO_CHOICE = 'none';
/** `recipientName` is 1–200 characters; `receiptEvidence` and `note` at most 2000. */
const MAXIMUM_RECIPIENT = 200;
const MAXIMUM_NOTE = 2000;

/**
 * A run isolated inside a PLAIN string, where `<bdi>` cannot go — an option in
 * a select. U+2068 FIRST STRONG ISOLATE … U+2069 POP DIRECTIONAL ISOLATE is the
 * text form of `<bdi>`: a Latin name or an Arabic date keeps its own direction
 * inside a sentence of the other.
 */
/** U+2068 FIRST STRONG ISOLATE and U+2069 POP DIRECTIONAL ISOLATE, built from their code points so no invisible character sits in the source. */
const FIRST_STRONG_ISOLATE = String.fromCodePoint(0x2068);
const POP_DIRECTIONAL_ISOLATE = String.fromCodePoint(0x2069);
const isolate = (text: string) => `${FIRST_STRONG_ISOLATE}${text}${POP_DIRECTIONAL_ISOLATE}`;

type StatusEvent = ReportDetailView['events'][number];
type AffectedParty = ReportDetailView['affectedParties'][number];

/** The statuses that make a delivered version "affected" (contract section 5.3). */
const AFFECTING: readonly ReportVersionStatus[] = [
  REPORT_VERSION_STATUS.SUSPENDED,
  REPORT_VERSION_STATUS.CANCELLED,
  REPORT_VERSION_STATUS.SUPERSEDED,
];

/**
 * The changes a notice can be about: every status change made to a version
 * AFTER it was issued — a suspension, a reinstatement, a cancellation, a
 * replacement. A change to a version that was never issued cannot be one (the
 * API refuses it with `status_event_not_of_issued_version`).
 */
export function noticeEvents(report: ReportDetailView): StatusEvent[] {
  return report.events.filter(
    (event) =>
      event.fromStatus === REPORT_VERSION_STATUS.ISSUED_VALID ||
      event.fromStatus === REPORT_VERSION_STATUS.SUSPENDED,
  );
}

/**
 * The change that made an affected party's version affected: the latest move of
 * that version to suspended, cancelled or superseded. Offered as the notice's
 * subject, and the person may choose another.
 */
export function affectingEvent(
  report: ReportDetailView,
  versionNumber: number,
): StatusEvent | null {
  // `events` is newest first.
  return (
    noticeEvents(report).find(
      (event) => event.versionNumber === versionNumber && AFFECTING.includes(event.toStatus),
    ) ?? null
  );
}

// ---------------------------------------------------------------------------
// Record a delivery or a notice
// ---------------------------------------------------------------------------

export function RecordDeliveryDialog({
  report,
  initialPurpose,
  initialRecipientName,
  initialEventId,
  onClose,
}: {
  readonly report: ReportDetailView;
  readonly initialPurpose: ReportDeliveryPurpose;
  readonly initialRecipientName?: string;
  readonly initialEventId?: string | null;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const { can } = useAuth();
  const locale = currentLocale();

  // A delivery hands over the version in force, so it needs one that is issued and valid.
  const deliveryPossible = report.status === REPORT_VERSION_STATUS.ISSUED_VALID;

  const [purpose, setPurpose] = useState<ReportDeliveryPurpose>(initialPurpose);
  const [method, setMethod] = useState<ReportDeliveryMethod | null>(null);
  const timeZone = useLaboratoryTimeZone();
  // An instant with the laboratory's offset, from the field; null until complete.
  const [deliveredAt, setDeliveredAt] = useState<string | null>(null);
  const [recipientName, setRecipientName] = useState(initialRecipientName ?? '');
  const [contactId, setContactId] = useState<string | null>(null);
  const [receiptConfirmed, setReceiptConfirmed] = useState(false);
  const [receiptEvidence, setReceiptEvidence] = useState('');
  const [note, setNote] = useState('');
  const [eventId, setEventId] = useState<string | null>(initialEventId ?? null);
  const [showErrors, setShowErrors] = useState(false);

  // The client's contacts are read only by someone who may read clients; for
  // anyone else the selector is simply absent and the name alone is recorded.
  const mayReadContacts = can(PERMISSIONS.CLIENTS_VIEW);
  const contacts = useQuery({
    queryKey: ['clients', report.client.id, 'contacts', { includeInactive: true }],
    queryFn: () => clientsApi.listContacts(report.client.id, true),
    enabled: mayReadContacts,
  });

  const record = useReportAct(
    (body: RecordReportDeliveryInput) => reportsApi.recordDelivery(report.id, body),
    (answer, body) => {
      // `deliveries` is newest first: the row just recorded, as the server stored it.
      const stored = answer.deliveries[0];
      show({
        tone: 'success',
        message:
          body.purpose === REPORT_DELIVERY_PURPOSE.NOTICE
            ? t('reports.delivery.noticeRecorded')
            : t('reports.delivery.recorded'),
        description: stored
          ? t('reports.delivery.recordedAgainst', { number: stored.versionNumber })
          : t('reports.delivery.systemSendsNothingTitle'),
      });
      onClose();
    },
  );

  const isNotice = purpose === REPORT_DELIVERY_PURPOSE.NOTICE;
  const instant = deliveredAt;
  const deliveredAtProblem =
    deliveredAt === null
      ? t('validation.required')
      : isTooFarAhead(deliveredAt)
        ? t('reports.refusal.delivered_at_in_future')
        : null;
  const trimmedName = recipientName.trim();
  const nameProblem =
    trimmedName === ''
      ? t('validation.required')
      : trimmedName.length > MAXIMUM_RECIPIENT
        ? t('validation.tooLong')
        : null;
  const trimmedEvidence = receiptEvidence.trim();
  const evidenceProblem = !receiptConfirmed
    ? null
    : trimmedEvidence === ''
      ? t('validation.required')
      : trimmedEvidence.length > MAXIMUM_NOTE
        ? t('validation.tooLong')
        : null;
  const trimmedNote = note.trim();
  const noteProblem = trimmedNote.length > MAXIMUM_NOTE ? t('validation.tooLong') : null;
  const methodProblem = method === null ? t('validation.required') : null;

  const submit = () => {
    setShowErrors(true);
    if (deliveredAtProblem || nameProblem || evidenceProblem || noteProblem || methodProblem) return;
    if (instant === null || method === null) return;
    const body: RecordReportDeliveryInput = {
      purpose,
      method,
      deliveredAt: instant,
      recipientName: trimmedName,
      receiptConfirmed,
      ...(contactId ? { clientContactId: contactId } : {}),
      // Evidence travels only with a confirmed receipt — it is what the confirmation rests on.
      ...(receiptConfirmed ? { receiptEvidence: trimmedEvidence } : {}),
      ...(trimmedNote !== '' ? { note: trimmedNote } : {}),
      // Only a notice refers to a status change.
      ...(isNotice && eventId ? { relatedStatusEventId: eventId } : {}),
    };
    // The schema the API validates with. A body it refuses is never sent.
    if (!recordReportDeliverySchema.safeParse(body).success) return;
    record.mutate(body);
  };

  const contactOptions = (contacts.data ?? []).map((contact) => {
    const name = contact.name ?? t('common.notRecorded');
    return {
      value: contact.id,
      label: contact.isActive
        ? name
        : t('reports.delivery.contactDeactivated', { name: isolate(name) }),
    };
  });

  const eventOptions = noticeEvents(report).map((event) => ({
    value: event.id,
    label: t('reports.delivery.eventOption', {
      number: event.versionNumber,
      from: event.fromStatus ? t(`status.reportVersion.${event.fromStatus}`) : '',
      to: t(`status.reportVersion.${event.toStatus}`),
      date: isolate(formatDateTime(event.actedAt, { locale }) ?? t('common.notRecorded')),
    }),
  }));

  return (
    <Dialog
      isOpen
      onClose={onClose}
      size="lg"
      title={isNotice ? t('reports.delivery.recordNotice') : t('reports.delivery.record')}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={record.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton priority="primary" isLoading={record.isPending} onPress={submit}>
            {t('reports.delivery.submit')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        <Alert
          tone="info"
          title={t('reports.delivery.systemSendsNothingTitle')}
          description={t('reports.delivery.systemSendsNothingBody')}
        />
        <p className="text-caption text-default-700">{t('reports.delivery.appendOnlyBody')}</p>

        <RadioField
          label={t('reports.delivery.purpose')}
          orientation="horizontal"
          value={purpose}
          onChange={(value) => setPurpose(value as ReportDeliveryPurpose)}
          options={ALL_REPORT_DELIVERY_PURPOSES.map((value) => ({
            value,
            label: t(`deliveryPurpose.${value}`),
            isDisabled: value === REPORT_DELIVERY_PURPOSE.DELIVERY && !deliveryPossible,
          }))}
        />
        {!isNotice && !deliveryPossible ? (
          <Alert tone="warning" title={t('reports.delivery.onlyCurrentValid')} />
        ) : null}
        {!isNotice && report.paymentHold.state === 'held' ? (
          <Alert
            tone="warning"
            title={t('reports.delivery.heldTitle')}
            description={t('reports.delivery.heldBody')}
          />
        ) : null}

        <RadioField
          label={t('reports.delivery.method')}
          value={method}
          onChange={(value) => setMethod(value as ReportDeliveryMethod)}
          isRequired
          options={ALL_REPORT_DELIVERY_METHODS.map((value) => ({
            value,
            label: t(`deliveryMethod.${value}`),
          }))}
          {...(showErrors && methodProblem ? { error: methodProblem } : {})}
        />
        <DateTimeField
          label={t('reports.delivery.deliveredAt')}
          description={t('reports.delivery.deliveredAtHint')}
          value={deliveredAt}
          onChange={setDeliveredAt}
          timeZone={timeZone}
          isRequired
          {...(showErrors && deliveredAtProblem ? { error: deliveredAtProblem } : {})}
        />
        <TextField
          label={t('reports.delivery.recipientName')}
          description={t('reports.delivery.recipientNameHint')}
          value={recipientName}
          onChange={setRecipientName}
          isRequired
          {...(showErrors && nameProblem ? { error: nameProblem } : {})}
        />
        {mayReadContacts && contacts.isError ? (
          <p className="text-caption text-default-700">{t('reports.delivery.contactsUnavailable')}</p>
        ) : null}
        {mayReadContacts && contactOptions.length > 0 ? (
          <SelectField
            label={t('reports.delivery.clientContact')}
            description={t('reports.delivery.clientContactHint')}
            value={contactId ?? NO_CHOICE}
            onChange={(value) => setContactId(value === NO_CHOICE ? null : value)}
            options={[{ value: NO_CHOICE, label: t('common.none') }, ...contactOptions]}
          />
        ) : null}
        {isNotice ? (
          <SelectField
            label={t('reports.delivery.relatedEvent')}
            description={t('reports.delivery.relatedEventHint')}
            value={eventId ?? NO_CHOICE}
            onChange={(value) => setEventId(value === NO_CHOICE ? null : value)}
            options={[{ value: NO_CHOICE, label: t('common.none') }, ...eventOptions]}
          />
        ) : null}

        <CheckboxField
          label={t('reports.delivery.receiptConfirmed')}
          description={t('reports.delivery.receiptConfirmedHint')}
          value={receiptConfirmed}
          onChange={setReceiptConfirmed}
        />
        {receiptConfirmed ? (
          <TextAreaField
            label={t('reports.delivery.receiptEvidence')}
            description={t('reports.delivery.receiptEvidenceHint')}
            value={receiptEvidence}
            onChange={setReceiptEvidence}
            isRequired
            rows={2}
            {...(showErrors && evidenceProblem ? { error: evidenceProblem } : {})}
          />
        ) : null}
        <TextAreaField
          label={t('reports.delivery.note')}
          value={note}
          onChange={setNote}
          rows={2}
          {...(showErrors && noteProblem ? { error: noteProblem } : {})}
        />

        {record.error ? <ReportRefusalNotice error={record.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// The panel's actions, and one notice per affected party
// ---------------------------------------------------------------------------

/**
 * "Record a delivery" and "Record a notice", from `can.deliver`.
 *
 * A delivery is offered only while the version in force is issued and valid —
 * the only state in which the server accepts one — and a notice whenever the
 * hint allows. The open dialog is held outside the hint check, so a reload
 * after a refusal cannot take the refusal away with the button.
 */
export function DeliveryActions({ report }: { readonly report: ReportDetailView }) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<ReportDeliveryPurpose | null>(null);
  const deliveryPossible = report.status === REPORT_VERSION_STATUS.ISSUED_VALID;

  return (
    <>
      {report.can.deliver ? (
        <div className="flex flex-col gap-inline">
          <div className="flex flex-wrap items-center gap-inline" data-testid="delivery-actions">
            {deliveryPossible ? (
              <ActionButton
                priority="secondary"
                size="sm"
                onPress={() => setDialog(REPORT_DELIVERY_PURPOSE.DELIVERY)}
              >
                {t('reports.delivery.record')}
              </ActionButton>
            ) : null}
            <ActionButton
              priority="secondary"
              size="sm"
              onPress={() => setDialog(REPORT_DELIVERY_PURPOSE.NOTICE)}
            >
              {t('reports.delivery.recordNotice')}
            </ActionButton>
          </div>
          {deliveryPossible ? null : (
            <p className="text-caption text-default-700">{t('reports.delivery.onlyCurrentValid')}</p>
          )}
        </div>
      ) : null}
      {dialog ? (
        <RecordDeliveryDialog
          key={dialog}
          report={report}
          initialPurpose={dialog}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}

/**
 * "Record a notice for this recipient": the same form, preset to a notice, the
 * recipient's name, and the change that made their version affected. Every
 * preset can be changed; the product informs nobody by itself.
 */
export function NoticeForParty({
  report,
  party,
  describedBy,
}: {
  readonly report: ReportDetailView;
  readonly party: AffectedParty;
  /** The id of the element naming the recipient, so each button says whom it is for. */
  readonly describedBy?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      {report.can.deliver ? (
        <ActionButton
          priority="secondary"
          size="sm"
          onPress={() => setOpen(true)}
          {...(describedBy ? { 'aria-describedby': describedBy } : {})}
        >
          {t('reports.delivery.noticeForParty')}
        </ActionButton>
      ) : null}
      {open ? (
        <RecordDeliveryDialog
          report={report}
          initialPurpose={REPORT_DELIVERY_PURPOSE.NOTICE}
          initialRecipientName={party.recipientName}
          initialEventId={affectingEvent(report, party.versionNumber)?.id ?? null}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Release the payment hold
// ---------------------------------------------------------------------------

export function ReleasePaymentHoldDialog({
  report,
  onClose,
}: {
  readonly report: ReportDetailView;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const problem = textProblem(note, MAXIMUM_REASON);

  const release = useReportAct(
    (body: { note: string }) => reportsApi.releasePaymentHold(report.id, body),
    (answer) => {
      show({
        tone: 'success',
        message:
          answer.paymentHold.state === 'released'
            ? t('reports.paymentHold.released')
            : t(`paymentHold.${answer.paymentHold.state}`),
        // The report's status, as the server now reads it: a release moves access, nothing else.
        description: t('reports.lifecycle.statusNow', {
          status: t(`status.report.${answer.status}`),
        }),
      });
      onClose();
    },
  );

  const submit = () => {
    setTouched(true);
    const body = { note: note.trim() };
    // The schema the API validates with. A body it refuses is never sent.
    if (problem || !releasePaymentHoldSchema.safeParse(body).success) return;
    release.mutate(body);
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('reports.paymentHold.release')}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={release.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority="primary"
            // Mandatory here AND on the server, which refuses a release without one.
            isDisabled={problem !== null}
            isLoading={release.isPending}
            onPress={submit}
          >
            {t('reports.paymentHold.release')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        <Alert
          tone="info"
          title={t('reports.paymentHold.changesNothingTitle')}
          description={t('reports.paymentHold.changesNothingBody')}
        />
        <p className="text-caption text-default-700">{t('reports.paymentHold.releaseOnce')}</p>
        <TextAreaField
          label={t('reports.paymentHold.releaseNote')}
          description={t('reports.paymentHold.releaseNoteHint')}
          value={note}
          onChange={(value) => {
            setNote(value);
            setTouched(true);
          }}
          isRequired
          rows={3}
          {...(touched && problem ? { error: t(problem) } : {})}
        />
        {release.error ? <ReportRefusalNotice error={release.error} /> : null}
      </div>
    </Dialog>
  );
}

/** "Release the hold", from `can.releasePaymentHold`. The dialog outlives the hint, as above. */
export function ReleasePaymentHoldAction({ report }: { readonly report: ReportDetailView }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      {report.can.releasePaymentHold ? (
        <div className="border-t border-default-100 pt-3">
          <ActionButton priority="secondary" size="sm" onPress={() => setOpen(true)}>
            {t('reports.paymentHold.release')}
          </ActionButton>
        </div>
      ) : null}
      {open ? <ReleasePaymentHoldDialog report={report} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
